import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from '@tanstack/react-router';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { useCancelBooking } from '@/hooks/api/use-cancel-booking';
import { participantBookingQueryOptions } from '@/hooks/api/use-participant-booking';

import {
  BookingBlocked,
  BookingPageShell,
} from './-components/booking/booking-page-shell';
import { CancelBookingForm } from './-components/booking/cancel-booking-form';
import {
  formatBookingDateTime,
  formatTimezoneName,
  hoursUntil,
} from './-components/booking/format-booking';

/**
 * Cancel a booked session: the legacy
 * `/respondents/schedule/:bookingUid/cancellation/:id` page. Lands on the
 * participant's projects afterwards, as the legacy page did.
 */
export const Route = createFileRoute(
  '/participants/_authenticated/projects/$projectId/cancel-booking',
)({
  loader: async ({ context, params }) => {
    // The authenticated layout has already turned away anyone without a
    // user; the root context type still admits null.
    const userId = context.user?.id;
    if (userId) {
      await context.queryClient.ensureQueryData(
        participantBookingQueryOptions(params.projectId, userId),
      );
    }
  },
  component: CancelBookingPage,
});

const authenticatedRoute = getRouteApi('/participants/_authenticated');

function CancelBookingPage() {
  const { projectId } = Route.useParams();
  const { user } = authenticatedRoute.useRouteContext();
  const navigate = useNavigate();
  const booking = useQuery(participantBookingQueryOptions(projectId, user.id));
  const cancel = useCancelBooking();

  const data = booking.data;
  const slot = data?.booking;

  return (
    <BookingPageShell projectId={projectId}>
      {!data ? (
        <LoadingIndicator />
      ) : !slot || data.status !== 'SCHEDULED' ? (
        <BookingBlocked reason="NO_BOOKING" />
      ) : (
        <CancelBookingForm
          projectName={data.project.name}
          hostFirstName={data.event?.hostFirstName}
          formattedDateTime={formatBookingDateTime(slot.start, slot.timezone)}
          formattedTimezone={formatTimezoneName(slot.start, slot.timezone)}
          isLateCancellation={hoursUntil(slot.start) < data.lateCancelHours}
          lateCancelHours={data.lateCancelHours}
          isRemote={data.project.researchConductType !== 1}
          isSubmitting={cancel.isPending}
          onSubmit={(values) =>
            cancel.mutate(
              { projectId, userId: user.id, ...values },
              {
                onSuccess: () => {
                  void navigate({ to: '/participants/my-projects' });
                },
              },
            )
          }
        />
      )}
    </BookingPageShell>
  );
}
